import { FC, HTMLAttributes, ReactNode, RefAttributes } from 'react'
import styled from 'styled-components'

import {
  Carat,
  Button as LMFButton,
  RotateClockwise,
  TypeStyle,
  fromMd,
} from '@syconium/little-miss-figgy'

import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'

/**
 * A wrapper for the embroidery form header components
 */
const Wrapper: FC<HTMLAttributes<HTMLDivElement>> = ({ children, ...rest }) => {
  return <Wrap {...rest}>{children}</Wrap>
}

/**
 *  A back button for the embroidery form header.
 *
 * @param props
 * @param props.handleClick - Callback on button click
 * @param props.text - Text to display
 */

export type EmbroideryFormBackButtonProps = {
  text: string
  handleClick: (index: number | null) => void
} & HTMLAttributes<HTMLButtonElement>

const BackButton: FC<EmbroideryFormBackButtonProps> = ({ children, text, handleClick }) => {
  const {
    embroidery: { modal: strings },
  } = useTranslation()
  return (
    <Button
      aria-label='back'
      borderless={true}
      variant={'ghost'}
      size={'small'}
      onClick={() => handleClick(null)}
    >
      <Carat rotateClockwise={RotateClockwise.Half} />
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
      <ButtonText>{text ?? strings.backButton}</ButtonText>
      {children}
    </Button>
  )
}

/**
 * A title component for the embroidery form header
 */
const Title = ({ children, ref }: { children?: ReactNode } & RefAttributes<HTMLHeadingElement>) => {
  return (
    <TitleWrap ref={ref} as={'h2'} tabIndex={-1}>
      {children}
    </TitleWrap>
  )
}

/**
 * A price component for the embroidery form header
 *
 * @param props
 * @param props.price - The price of the embroidery option.
 */
export type EmbroideryFormHeaderPriceProps = {
  price: string | undefined
  children?: ReactNode
}
const Price: FC<EmbroideryFormHeaderPriceProps> = ({ children, price }) => {
  return (
    <>
      <PriceWrap>+{price}</PriceWrap>
      {children}
    </>
  )
}

const Step: FC<{ step: string }> = ({ step }) => {
  return <StepIndicator>{step}</StepIndicator>
}

export const EmbroideryFormHeader = {
  Wrapper,
  BackButton,
  Title,
  Price,
  Step,
  Column: styled.div``,
}

const Wrap = styled.div`
  display: none;
  text-transform: uppercase;
  padding: ${o => o.theme.spacing(10, 6, 6, 6)};

  ${fromMd} {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
  }
`

const TitleWrap = styled.h1`
  ${TypeStyle.css.headlineExtraExtraSmall}
  &:only-child {
    margin: 0 auto;
  }
  && {
    letter-spacing: ${o => o.theme.typography.font.letterSpacing.wide};
  }
`

const PriceWrap = styled(TypeStyle.HeadlineExtraExtraSmall)`
  letter-spacing: ${o => o.theme.typography.font.letterSpacing.wide};
  min-width: 80px;
  text-align: right;
`

const Button = styled(LMFButton)`
  padding: 0 ${o => o.theme.spacing(4)};
  min-height: 0;
  min-width: 80px;
  justify-content: space-between;
`

const ButtonText = styled.span`
  font-size 13px;
  text-transform: capitalize;
  letter-spacing: 0.7px;
  line-height: 20px;
`

const StepIndicator = styled(TypeStyle.HeadlineExtraExtraSmall)`
  letter-spacing: ${o => o.theme.typography.font.letterSpacing.wide};
  min-width: 80px;
  text-align: right;
`
